<div class="p-8">
    <div class="flex justify-between items-center mb-4">
        <h1 class="text-2xl font-bold">Domains</h1>
        <button (click)="openAdd()" class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600">
            Add Domain
        </button>
    </div>

    @if (successMessage) {
        <div class="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            {{ successMessage }}
        </div>
    }
    @if (errorMessage) {
        <div class="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {{ errorMessage }}
        </div>
    }

    <app-spinner *ngIf="loading"></app-spinner>

    <div *ngIf="!loading" class="space-y-3">
        @if (domains.length === 0) {
            <div class="bg-white border rounded-lg p-6 text-center text-gray-500">
                No domains yet. Add one to start provisioning a certificate.
            </div>
        }
        @for (d of domains; track d._id) {
            <div class="bg-white border rounded-lg shadow">
                <div class="p-4 flex items-center justify-between gap-4">
                    <div class="flex items-center gap-3 min-w-0">
                        <span class="px-2 py-1 text-xs rounded font-medium" [ngClass]="statusClass(d.status)">
                            {{ statusLabel(d.status) }}
                        </span>
                        <div class="min-w-0">
                            <div class="font-semibold truncate">{{ d.domain }}</div>
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <button
                            (click)="refresh(d._id)"
                            [disabled]="refreshingId === d._id"
                            class="px-3 py-2 border rounded hover:bg-gray-50 text-sm disabled:opacity-50"
                            title="Check certificate status">
                            {{ refreshingId === d._id ? 'Refreshing…' : 'Refresh' }}
                        </button>
                        <button
                            (click)="toggleExpand(d._id)"
                            class="px-3 py-2 border rounded hover:bg-gray-50 text-sm">
                            {{ expandedId === d._id ? 'Hide' : 'Details' }}
                        </button>
                        <button
                            (click)="delete(d._id)"
                            [disabled]="deletingId === d._id"
                            class="px-3 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm disabled:opacity-50">
                            Delete
                        </button>
                    </div>
                </div>
                @if (expandedId === d._id) {
                    <div class="border-t px-4 py-3 bg-gray-50 text-sm">
                        @if (d.status === 'failed' && d.statusError) {
                            <div class="mb-3 text-red-700">Error: {{ d.statusError }}</div>
                        }
                        @if (siteIp) {
                            <div class="mb-2 font-medium">Site DNS records</div>
                            <div class="mb-2 text-gray-600">
                                Point the following A record(s) at the platform IP so the site is reachable.
                            </div>
                            <table class="w-full border-collapse mb-4">
                                <thead>
                                    <tr class="text-left text-xs uppercase text-gray-500">
                                        <th class="py-1 pr-2">Type</th>
                                        <th class="py-1 pr-2">Name</th>
                                        <th class="py-1 pr-2">Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr class="border-t">
                                        <td class="py-2 pr-2 font-mono align-top">A</td>
                                        <td class="py-2 pr-2 font-mono break-all align-top">
                                            <span>{{ d.domain }}</span>
                                            <button (click)="copy(d.domain)" class="text-blue-600 hover:text-blue-800 ml-2 align-middle" title="Copy name">
                                                <svg class="w-[21px] h-[21px] inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                </svg>
                                            </button>
                                        </td>
                                        <td class="py-2 pr-2 font-mono break-all align-top">
                                            <span>{{ siteIp }}</span>
                                            <button (click)="copy(siteIp)" class="text-blue-600 hover:text-blue-800 ml-2 align-middle" title="Copy value">
                                                <svg class="w-[21px] h-[21px] inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                    @if (d.redirectWww) {
                                        <tr class="border-t">
                                            <td class="py-2 pr-2 font-mono align-top">A</td>
                                            <td class="py-2 pr-2 font-mono break-all align-top">
                                                <span>www.{{ d.domain }}</span>
                                                <button (click)="copy('www.' + d.domain)" class="text-blue-600 hover:text-blue-800 ml-2 align-middle" title="Copy name">
                                                    <svg class="w-[21px] h-[21px] inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                    </svg>
                                                </button>
                                            </td>
                                            <td class="py-2 pr-2 font-mono break-all align-top">
                                                <span>{{ siteIp }}</span>
                                                <button (click)="copy(siteIp)" class="text-blue-600 hover:text-blue-800 ml-2 align-middle" title="Copy value">
                                                    <svg class="w-[21px] h-[21px] inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        }
                        @if (statusLabel(d.status) !== 'Issued' && d.validationRecords.length > 0) {
                            <div class="mb-2 font-medium">DNS validation records</div>
                            <div class="mb-2 text-gray-600">
                                Add the following CNAME records to your DNS provider. Validation can take hours;
                                click Refresh once the records have propagated.
                            </div>
                            <table class="w-full border-collapse">
                                <thead>
                                    <tr class="text-left text-xs uppercase text-gray-500">
                                        <th class="py-1 pr-2">Type</th>
                                        <th class="py-1 pr-2">Name</th>
                                        <th class="py-1 pr-2">Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    @for (r of d.validationRecords; track r.name) {
                                        <tr class="border-t">
                                            <td class="py-2 pr-2 font-mono align-top">{{ r.type }}</td>
                                            <td class="py-2 pr-2 font-mono break-all align-top">
                                                <span>{{ r.name }}</span>
                                                <button (click)="copy(r.name)" class="text-blue-600 hover:text-blue-800 ml-2 align-middle" title="Copy name">
                                                    <svg class="w-[21px] h-[21px] inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                    </svg>
                                                </button>
                                            </td>
                                            <td class="py-2 pr-2 font-mono break-all align-top">
                                                <span>{{ r.value }}</span>
                                                <button (click)="copy(r.value)" class="text-blue-600 hover:text-blue-800 ml-2 align-middle" title="Copy value">
                                                    <svg class="w-[21px] h-[21px] inline" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path>
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    }
                                </tbody>
                            </table>
                        } @else if (!d.ownsCert) {
                            <div class="text-gray-600">
                                No DNS validation required — an existing certificate in ACM covers this domain.
                            </div>
                        }
                        <div class="mt-3 text-xs text-gray-500">
                            Certificate ARN: <span class="font-mono break-all">{{ d.certificateArn }}</span>
                        </div>
                        @if (d.lastCheckedAt) {
                            <div class="text-xs text-gray-500">Last checked: {{ d.lastCheckedAt }}</div>
                        }
                    </div>
                }
            </div>
        }
    </div>
</div>

<div *ngIf="showAddModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div class="bg-white rounded-lg max-w-lg w-full mx-4">
        <div class="p-6">
            <h2 class="text-xl font-bold mb-4">Add Domain</h2>
            <form [formGroup]="addForm" (ngSubmit)="submitAdd()">
                <label class="block mb-2 text-sm font-medium">Domain (e.g. example.com)</label>
                <input
                    type="text"
                    formControlName="domain"
                    class="w-full px-3 py-2 border rounded mb-1"
                    placeholder="example.com"
                    autocomplete="off" />
                <div *ngIf="addForm.get('domain')?.invalid && addForm.get('domain')?.touched" class="text-xs text-red-600 mb-3">
                    Enter a valid apex or subdomain (without the <code>www.</code> prefix).
                </div>
                <label class="flex items-center gap-2 mt-3 mb-5">
                    <input type="checkbox" formControlName="redirectWww" />
                    <span class="text-sm">Include www in certificate</span>
                </label>
                <p class="text-xs text-gray-500 -mt-4 mb-5">
                    Adds <code>www.&lt;domain&gt;</code> as a SAN on the certificate so browsers don't show a
                    TLS warning on the www host. The frontend decides what to serve there.
                </p>
                <div class="flex justify-end gap-2">
                    <button type="button" (click)="closeAdd()" class="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
                    <button
                        type="submit"
                        [disabled]="addForm.invalid || submitting"
                        class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50">
                        {{ submitting ? 'Adding…' : 'Add Domain' }}
                    </button>
                </div>
            </form>
        </div>
    </div>
</div>

<app-confirmation-modal
  [show]="showDeleteModal"
  title="Delete Domain"
  message="Delete this domain? The ACM certificate will also be removed if we own it."
  confirmText="Delete"
  (confirm)="confirmDelete()"
  (cancel)="cancelDelete()">
</app-confirmation-modal>
